import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, MapPin } from "lucide-react";

const VAN_IMG = "https://images.unsplash.com/photo-1587813369290-091c9d432daf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxjYXJnbyUyMGxvZ2lzdGljcyUyMHZhbiUyMGRlbGl2ZXJ5JTIwYXNzb2NpYXRlJTIwZmxlZXR8ZW58MHx8fHwxNzkxMDYyMzY1fDA&ixlib=rb-4.1.0&q=85";

const lines = [
  { text: "MANPOWER THAT", cls: "text-slate-100" },
  { text: "MOVES AT", cls: "text-slate-100" },
  { text: "DELIVERY SPEED", cls: "text-[#d4af37]" },
];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.35 + i * 0.16, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex items-center overflow-hidden hero-grid-bg">
      <motion.div style={{ y: bgY }} className="absolute inset-0 pointer-events-none">
        <svg className="absolute inset-0 w-full h-full opacity-30" preserveAspectRatio="none" viewBox="0 0 1200 800">
          <path className="route-line" d="M-50 650 C 250 520, 420 700, 700 480 S 1100 300, 1300 380" fill="none" stroke="#3b82f6" strokeWidth="1.5" />
          <path className="route-line" d="M-50 200 C 300 320, 520 120, 850 260 S 1150 480, 1300 420" fill="none" stroke="#d4af37" strokeWidth="1.5" style={{ animationDuration: "18s" }} />
        </svg>
        <div className="absolute -top-40 -right-40 w-[42rem] h-[42rem] rounded-full bg-[#1e40af]/25 blur-[130px]" />
        <div className="absolute bottom-0 -left-40 w-[30rem] h-[30rem] rounded-full bg-[#d4af37]/10 blur-[120px]" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-20 grid lg:grid-cols-12 gap-12 items-center w-full">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7 }}
            className="inline-flex items-center gap-2.5 border border-white/15 bg-white/5 backdrop-blur-md rounded-full px-4 py-2 mb-8"
            data-testid="hero-status-ticker"
          >
            <span className="w-2 h-2 rounded-full bg-emerald-400 pulse-dot" />
            <span className="text-xs font-semibold tracking-[0.18em] uppercase text-slate-300">
              Operational — Takhatgarh, Pali · Deployment Ready
            </span>
          </motion.div>

          <h1 data-testid="hero-heading" className="font-display font-extrabold uppercase leading-[0.92] tracking-tight text-5xl sm:text-7xl lg:text-8xl">
            {lines.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span custom={i} variants={lineVariants} initial="hidden" animate="visible" className={`block ${l.cls}`}>
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.8 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed"
          >
            Verified, high-performance blue-collar logistics manpower. In-store staff and van delivery associates with local route expertise, 100% document verification, and a 4-hour replacement guarantee — built for high-volume sale events.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#enquire"
              data-testid="btn-hire-now"
              className="group inline-flex items-center gap-3 bg-[#d4af37] text-[#0a0f1d] font-bold uppercase tracking-wider text-sm px-7 py-4 rounded-sm hover:bg-[#f59e0b] transition-colors duration-300"
            >
              Hire Manpower Now
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1.5 transition-transform duration-300" />
            </a>
            <a
              href="#join"
              data-testid="btn-apply-job"
              className="inline-flex items-center gap-3 border border-white/25 text-slate-200 font-bold uppercase tracking-wider text-sm px-7 py-4 rounded-sm hover:border-[#d4af37] hover:text-[#d4af37] transition-colors duration-300"
            >
              Apply for Jobs
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-xs text-slate-500 uppercase tracking-[0.15em] font-semibold"
          >
            <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#d4af37]" /> Aadhar · PAN · Bank Verified</span>
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#3b82f6]" /> Local Route Expertise</span>
          </motion.div>
        </div>

        <motion.div style={{ y: imgY }} className="lg:col-span-5 relative hidden lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, rotate: 2 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ delay: 0.9, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-3 border border-[#d4af37]/30 rounded-sm" />
            <div className="relative overflow-hidden rounded-sm">
              <img src={VAN_IMG} alt="Delivery van fleet" className="w-full h-[30rem] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1d]/85 via-transparent to-[#0a0f1d]/20" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                <div>
                  <p className="font-display text-2xl font-bold uppercase text-slate-100 leading-none">Van Delivery</p>
                  <p className="font-display text-2xl font-bold uppercase text-[#d4af37] leading-none">Associates</p>
                </div>
                <span className="text-[10px] font-bold tracking-[0.2em] uppercase bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 px-2.5 py-1.5 rounded-sm">
                  100% Verified
                </span>
              </div>
            </div>
            <div className="absolute -top-5 -right-5 bg-[#0a0f1d] border border-white/10 px-4 py-3 rounded-sm shadow-2xl">
              <p className="font-display text-3xl font-extrabold text-[#d4af37] leading-none">48h</p>
              <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mt-1">Scale-Up Window</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
